import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useInfiniteQuery } from '@tanstack/react-query'

import { useAuth } from '@/features/auth'
import { api, ApiError } from '@/shared/api'
import { routes } from '@/shared/config'
import { typographyStyle } from '@/shared/lib/catalog'
import { designTokens } from '@/shared/lib/design-tokens'
import { Button } from '@/shared/ui'
import { SuperAdminTabNav } from './super-admin-tab-nav'

type Severity = 'info' | 'warn' | 'error' | 'critical'

type SystemEvent = {
  id: string
  eventType: string
  eventCode: string
  severity: Severity
  source: string
  occurredAt: string
  message: string
  metadata: Record<string, unknown>
  correlationId?: string
  releaseName?: string
  buildSha?: string
  actorType?: string
  actorId?: string
}

type SystemEventsResponse = {
  data: SystemEvent[]
  meta: {
    nextCursor?: string
    limit: number
    hasMore: boolean
  }
}

type FilterState = {
  severity: '' | Severity
  eventType: string
  eventCode: string
  source: string
  from: string
  to: string
}

const defaultFilters: FilterState = {
  severity: '',
  eventType: '',
  eventCode: '',
  source: '',
  from: '',
  to: '',
}

const titleStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.strong,
  ...typographyStyle(designTokens.typography.heading.h2),
}

const subtitleStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.primary,
  ...typographyStyle(designTokens.typography.body.base),
}

const filterInputStyle = {
  border: `${designTokens.borders.widths.thin} ${designTokens.borders.styles.solid} ${designTokens.colors.border.inputDefault}`,
  borderRadius: designTokens.borders.radius.sm,
  backgroundColor: designTokens.colors.background.surface,
  color: designTokens.colors.text.primary,
  minHeight: designTokens.sizes.height.paymentSummaryPromoInput,
  padding: `${designTokens.spacing.xs} ${designTokens.spacing.sm}`,
  fontFamily: designTokens.typography.fontFamilies.primary,
  ...typographyStyle(designTokens.typography.body.sm),
}

const tableHeadStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.strong,
  ...typographyStyle(designTokens.typography.body.baseSemibold),
}

const cellStyle = {
  borderBottom: `${designTokens.borders.widths.thin} ${designTokens.borders.styles.solid} ${designTokens.colors.border.adminPattern}`,
  padding: `${designTokens.spacing.sm} ${designTokens.spacing.xs}`,
  verticalAlign: 'top' as const,
  fontFamily: designTokens.typography.fontFamilies.primary,
  ...typographyStyle(designTokens.typography.body.sm),
  color: designTokens.colors.text.primary,
}

const severityBadge = (severity: Severity) => {
  switch (severity) {
    case 'critical':
      return { background: '#fee2e2', color: '#991b1b' }
    case 'error':
      return { background: '#ffedd5', color: '#9a3412' }
    case 'warn':
      return { background: '#fef9c3', color: '#854d0e' }
    default:
      return { background: '#dbeafe', color: '#1e3a8a' }
  }
}

const toIsoIfPresent = (value: string) => {
  if (!value) {
    return undefined
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return undefined
  }
  return date.toISOString()
}

const formatDate = (value: string) =>
  new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  }).format(new Date(value))

const loadSystemEvents = async (
  filters: FilterState,
  cursor: string | undefined
): Promise<SystemEventsResponse> => {
  const params: Record<string, string> = { limit: '50' }

  if (cursor) {
    params.cursor = cursor
  }
  if (filters.severity) {
    params.severity = filters.severity
  }
  if (filters.eventType) {
    params.eventType = filters.eventType
  }
  if (filters.eventCode) {
    params.eventCode = filters.eventCode
  }
  if (filters.source) {
    params.source = filters.source
  }

  const from = toIsoIfPresent(filters.from)
  if (from) {
    params.from = from
  }
  const to = toIsoIfPresent(filters.to)
  if (to) {
    params.to = to
  }

  return api.get<SystemEventsResponse>('/api/v1/admin/system-events', { params })
}

export function SuperAdminLogsPage() {
  const { logout } = useAuth()
  const [draftFilters, setDraftFilters] = useState<FilterState>(defaultFilters)
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(defaultFilters)

  const eventsQuery = useInfiniteQuery({
    queryKey: ['super-admin-system-events', appliedFilters],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => loadSystemEvents(appliedFilters, pageParam),
    getNextPageParam: (lastPage) => lastPage.meta.nextCursor ?? undefined,
  })

  const events = useMemo(
    () => eventsQuery.data?.pages.flatMap((page) => page.data) ?? [],
    [eventsQuery.data]
  )

  const applyFilters = () => {
    setAppliedFilters({ ...draftFilters })
  }

  const resetFilters = () => {
    setDraftFilters(defaultFilters)
    setAppliedFilters(defaultFilters)
  }

  const renderError = () => {
    if (!eventsQuery.error) {
      return null
    }

    const message =
      eventsQuery.error instanceof ApiError
        ? `${eventsQuery.error.status} ${eventsQuery.error.statusText}`
        : 'Unable to load logs'

    return (
      <div
        style={{
          borderRadius: designTokens.borders.radius.sm,
          backgroundColor: '#fee2e2',
          color: '#7f1d1d',
          padding: designTokens.spacing.md,
          fontFamily: designTokens.typography.fontFamilies.primary,
          ...typographyStyle(designTokens.typography.body.base),
        }}
      >
        {message}
      </div>
    )
  }

  return (
    <section
      className="mx-auto flex w-full max-w-[1360px] flex-col px-4 py-6 lg:px-8 lg:py-8"
      style={{ gap: designTokens.spacing.lg }}
    >
      <header className="flex w-full flex-col" style={{ gap: designTokens.spacing.sm }}>
        <div
          className="flex flex-wrap items-center justify-between"
          style={{ gap: designTokens.spacing.sm }}
        >
          <Link to={routes.dashboardLk}>
            <Button type="button" variant="secondaryAssist">
              Back To Dashboard
            </Button>
          </Link>
          <Button type="button" variant="secondaryAssist" onClick={logout}>
            Logout
          </Button>
        </div>
        <SuperAdminTabNav activeTab="logs" />
        <h1 style={titleStyle}>Super Admin Logs</h1>
        <p style={subtitleStyle}>
          Critical operational events for release/deploy, SQL migration, env sync, and backend
          errors.
        </p>
      </header>

      <section
        className="grid w-full grid-cols-1 md:grid-cols-2 xl:grid-cols-4"
        style={{ gap: designTokens.spacing.sm }}
      >
        <select
          value={draftFilters.severity}
          onChange={(event) =>
            setDraftFilters((prev) => ({
              ...prev,
              severity: event.target.value as FilterState['severity'],
            }))
          }
          style={filterInputStyle}
        >
          <option value="">All severities</option>
          <option value="info">info</option>
          <option value="warn">warn</option>
          <option value="error">error</option>
          <option value="critical">critical</option>
        </select>
        <input
          value={draftFilters.eventType}
          onChange={(event) =>
            setDraftFilters((prev) => ({ ...prev, eventType: event.target.value }))
          }
          placeholder="eventType (e.g. db.migration)"
          style={filterInputStyle}
        />
        <input
          value={draftFilters.eventCode}
          onChange={(event) =>
            setDraftFilters((prev) => ({ ...prev, eventCode: event.target.value }))
          }
          placeholder="eventCode (e.g. release.deployed)"
          style={filterInputStyle}
        />
        <input
          value={draftFilters.source}
          onChange={(event) => setDraftFilters((prev) => ({ ...prev, source: event.target.value }))}
          placeholder="source (e.g. deploy-backend.sh)"
          style={filterInputStyle}
        />
        <input
          type="datetime-local"
          value={draftFilters.from}
          onChange={(event) => setDraftFilters((prev) => ({ ...prev, from: event.target.value }))}
          style={filterInputStyle}
        />
        <input
          type="datetime-local"
          value={draftFilters.to}
          onChange={(event) => setDraftFilters((prev) => ({ ...prev, to: event.target.value }))}
          style={filterInputStyle}
        />
        <Button
          type="button"
          onClick={applyFilters}
          style={{ minHeight: designTokens.sizes.height.paymentSummaryPromoInput }}
        >
          Apply Filters
        </Button>
        <Button
          type="button"
          variant="secondaryAssist"
          onClick={resetFilters}
          style={{ minHeight: designTokens.sizes.height.paymentSummaryPromoInput }}
        >
          Reset
        </Button>
      </section>

      {renderError()}

      <div
        className="w-full overflow-x-auto"
        style={{
          borderRadius: designTokens.borders.radius.sm,
          border: `${designTokens.borders.widths.thin} ${designTokens.borders.styles.solid} ${designTokens.colors.border.adminPattern}`,
          backgroundColor: designTokens.colors.background.surface,
        }}
      >
        <table className="w-full min-w-[980px]" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th align="left" style={cellStyle}>
                <span style={tableHeadStyle}>Date (UTC)</span>
              </th>
              <th align="left" style={cellStyle}>
                <span style={tableHeadStyle}>Type / Code</span>
              </th>
              <th align="left" style={cellStyle}>
                <span style={tableHeadStyle}>Severity</span>
              </th>
              <th align="left" style={cellStyle}>
                <span style={tableHeadStyle}>Source</span>
              </th>
              <th align="left" style={cellStyle}>
                <span style={tableHeadStyle}>Message</span>
              </th>
              <th align="left" style={cellStyle}>
                <span style={tableHeadStyle}>Details</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {eventsQuery.isLoading ? (
              <tr>
                <td colSpan={6} style={cellStyle}>
                  Loading events...
                </td>
              </tr>
            ) : events.length === 0 ? (
              <tr>
                <td colSpan={6} style={cellStyle}>
                  No events found for the selected filters.
                </td>
              </tr>
            ) : (
              events.map((event) => {
                const badge = severityBadge(event.severity)
                return (
                  <tr key={event.id}>
                    <td style={cellStyle}>{formatDate(event.occurredAt)}</td>
                    <td style={cellStyle}>
                      <div>{event.eventType}</div>
                      <code>{event.eventCode}</code>
                    </td>
                    <td style={cellStyle}>
                      <span
                        style={{
                          backgroundColor: badge.background,
                          color: badge.color,
                          borderRadius: designTokens.borders.radius.sm,
                          padding: `2px ${designTokens.spacing.xs}`,
                          fontWeight: 600,
                        }}
                      >
                        {event.severity}
                      </span>
                    </td>
                    <td style={cellStyle}>
                      <div>{event.source}</div>
                      {event.releaseName ? <div>release: {event.releaseName}</div> : null}
                    </td>
                    <td style={cellStyle}>{event.message}</td>
                    <td style={cellStyle}>
                      <details>
                        <summary>metadata</summary>
                        <pre className="mt-2 max-w-[420px] overflow-auto whitespace-pre-wrap break-words">
                          {JSON.stringify(event.metadata, null, 2)}
                        </pre>
                      </details>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="flex w-full justify-end">
        <Button
          type="button"
          variant="secondaryAssist"
          disabled={!eventsQuery.hasNextPage || eventsQuery.isFetchingNextPage}
          onClick={() => void eventsQuery.fetchNextPage()}
        >
          {eventsQuery.isFetchingNextPage
            ? 'Loading…'
            : eventsQuery.hasNextPage
              ? 'Load More'
              : 'No More Events'}
        </Button>
      </div>
    </section>
  )
}

SuperAdminLogsPage.displayName = 'SuperAdminLogsPage'
